/**
 * useExactLocation — shared client-side hook for the "share exact
 * location" button used in HeroChat and the authenticated chat
 * composer.
 *
 * What it does
 * -----------
 * • Tracks per-tab state (idle | requesting | granted | denied | error)
 * • Persists the last granted coords + timestamp in localStorage
 *   (key: `rammy_exact_location`) so a user who opted in once doesn't
 *   have to re-tap the button on every message.
 * • Auto-expires stored coords after ``STALE_MS`` (default 15 min)
 *   so a phone/laptop that has moved doesn't send a stale location.
 * • Exposes ``getHeaders()`` — used inside SSE POST calls to inject
 *   ``X-User-Lat`` / ``X-User-Lng`` / ``X-User-Accuracy`` headers.
 *   Returns an empty object when no fresh coords are available so
 *   the caller can spread it unconditionally.
 *
 * The setting toggle in Settings › Privacy uses the same localStorage
 * key so revoking there instantly stops the headers from being sent.
 */
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "rammy_exact_location";
const STALE_MS = 15 * 60 * 1000;   // 15 minutes
const CONSENT_KEY = "rammy_exact_location_consent";

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data.lat !== "number" || typeof data.lng !== "number") return null;
    if (!data.ts || Date.now() - data.ts > STALE_MS) return null;
    return data;
  } catch {
    return null;
  }
}

function writeStored(data) {
  try {
    if (data) localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* no-op — some browsers block storage in privacy mode */
  }
}

export function readConsent() {
  try { return localStorage.getItem(CONSENT_KEY) === "1"; } catch { return false; }
}

export function writeConsent(on) {
  try {
    if (on) localStorage.setItem(CONSENT_KEY, "1");
    else localStorage.removeItem(CONSENT_KEY);
  } catch { /* no-op */ }
}

export default function useExactLocation() {
  const [status, setStatus] = useState("idle"); // idle|requesting|granted|denied|error|stale
  const [coords, setCoords] = useState(() => readStored());
  const [error, setError] = useState(null);

  // Hydrate on mount — reflect any coords from a previous tab.
  useEffect(() => {
    const stored = readStored();
    if (stored) {
      setCoords(stored);
      setStatus("granted");
    }
  }, []);

  const request = useCallback(async () => {
    if (!navigator?.geolocation) {
      setStatus("error");
      setError("Your browser doesn't support geolocation.");
      return null;
    }
    setStatus("requesting");
    setError(null);
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const data = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            ts: Date.now(),
          };
          writeStored(data);
          writeConsent(true);
          setCoords(data);
          setStatus("granted");
          resolve(data);
        },
        (err) => {
          const denied = err && err.code === err.PERMISSION_DENIED;
          setStatus(denied ? "denied" : "error");
          setError(err?.message || "Couldn't fetch your location.");
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 8_000, maximumAge: 60_000 },
      );
    });
  }, []);

  const clear = useCallback(() => {
    writeStored(null);
    writeConsent(false);
    setCoords(null);
    setStatus("idle");
    setError(null);
  }, []);

  const getHeaders = useCallback(() => {
    const fresh = readStored();
    if (!fresh) return {};
    return {
      "X-User-Lat": String(fresh.lat),
      "X-User-Lng": String(fresh.lng),
      "X-User-Accuracy": String(Math.round(fresh.accuracy || 0)),
    };
  }, []);

  return { status, coords, error, request, clear, getHeaders };
}
